import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon
  const [impersonating, setImpersonating] = useState(
    () => JSON.parse(localStorage.getItem("cx_impersonating") || "null")
  );

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("cx_access_token");
    if (!token) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.data.user);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const persistTokens = (d) => {
    if (d.access_token) localStorage.setItem("cx_access_token", d.access_token);
    if (d.refresh_token) localStorage.setItem("cx_refresh_token", d.refresh_token);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persistTokens(data.data);
    setUser(data.data.user);
    return data.data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    persistTokens(data.data);
    setUser(data.data.user);
    return data.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("cx_access_token");
    localStorage.removeItem("cx_refresh_token");
    localStorage.removeItem("cx_impersonating");
    setImpersonating(null);
    setUser(false);
  };

  const startImpersonation = async (tenantId, tenantName) => {
    const { data } = await api.post(`/superadmin/tenants/${tenantId}/impersonate`);
    localStorage.setItem("cx_access_token", data.data.access_token);
    const info = { tenant_id: tenantId, tenant_name: tenantName };
    localStorage.setItem("cx_impersonating", JSON.stringify(info));
    setImpersonating(info);
    await loadMe();
  };

  const stopImpersonation = async () => {
    localStorage.removeItem("cx_impersonating");
    setImpersonating(null);
    // re-login required to drop tenant claim; simplest: force reload of me with fresh token via refresh
    const refresh = localStorage.getItem("cx_refresh_token");
    if (refresh) {
      try {
        const { data } = await api.post("/auth/refresh", { refresh_token: refresh });
        localStorage.setItem("cx_access_token", data.data.access_token);
      } catch (e) {}
    }
    await loadMe();
  };

  const value = { user, setUser, login, register, logout, loadMe,
    impersonating, startImpersonation, stopImpersonation, apiError };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
